import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { hydrateSlice, persistenceError, persistSlice, sliceHydrated, slicePersisted } from '../../persistence';
import {
    initRecipients,
    recipientsLoadedFromStorage,
    recipientsSaved,
    recipientsSaveError,
    removeRecipientsRequested,
    upsertRecipientsRequested,
} from '../actions/recipients.actions';
import { RECIPIENTS_FEATURE_KEY, SavedRecipientsState } from '../recipients-collection.state';

/**
 * Owns the recipients slice's storage sync. The NgRx store is the source of truth:
 * `saveRecipientsRequested` / `removeRecipientsRequested` mutate the slice first (via the
 * reducer), then this effect mirrors the collection to the unified slice storage via the
 * generic `persistSlice` / `hydrateSlice` layer. The generic `slicePersisted` /
 * `persistenceError` / `sliceHydrated` outcomes are bridged back to the recipients-specific
 * settle actions so `recipients-write-bridge.ts`'s correlation-id write contract works.
 */
@Injectable()
export class RecipientsEffects {
    public constructor(
        private readonly actions$: Actions,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    /** Rehydrate the persisted collection into the slice on init, via the generic layer. */
    public loadRecipients$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initRecipients),
            map(() => hydrateSlice({ featureKey: RECIPIENTS_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the recipients slice. */
    public hydrateRecipients$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === RECIPIENTS_FEATURE_KEY),
            map(({ data }) =>
                recipientsLoadedFromStorage({ entries: (data as SavedRecipientsState[] | undefined) ?? [] }),
            ),
        ),
    );

    /**
     * The store already holds the mutated collection; mirror it to storage via the generic
     * layer. This trigger list is hand-maintained — re-check it if a new mutating action is
     * added to the slice.
     */
    public persistRecipients$ = createEffect(() =>
        this.actions$.pipe(
            ofType(upsertRecipientsRequested, removeRecipientsRequested),
            map(({ correlationId }) => persistSlice({ featureKey: RECIPIENTS_FEATURE_KEY, correlationId })),
        ),
    );

    /** Bridge the generic persist success back to the recipients settle action. */
    public recipientsPersisted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(slicePersisted),
            filter(({ featureKey }) => featureKey === RECIPIENTS_FEATURE_KEY),
            map(({ correlationId }) => recipientsSaved({ correlationId })),
        ),
    );

    /** Bridge the generic persist failure back to the recipients settle action. */
    public recipientsPersistError$ = createEffect(() =>
        this.actions$.pipe(
            ofType(persistenceError),
            filter(({ featureKey }) => featureKey === RECIPIENTS_FEATURE_KEY),
            map(({ error, correlationId }) => {
                this.log.error(`RecipientsEffects: recipientsPersistError$: ${error}`);
                return recipientsSaveError({ error, correlationId });
            }),
        ),
    );

    /**
     * Carries the legacy service's inline `log.debug` calls for writes out of the service.
     * The legacy `removeRecipientsFromLocalStorage` log also included the array `index` it
     * spliced at — a storage-layout detail with no meaning in the store, deliberately
     * dropped here. The legacy "file was not found" debug line on a missing storage entry
     * is already covered by the generic `hydrateSlice` degrading to `sliceHydrated({ data:
     * undefined })` — no equivalent log is needed here for that case.
     */
    public logWrites$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(upsertRecipientsRequested, removeRecipientsRequested),
                map(({ type, notificationId }) =>
                    this.log.debug(`RecipientsEffects: logWrites$: ${type}`, { notificationId }),
                ),
            ),
        { dispatch: false },
    );
}
